﻿import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Upload, Download } from "lucide-react";
import { toast } from "react-toastify";

import Season from "@/components/Season/Season";
import Fetching from "@/components/Fetching/Fetching";
import CustomButton from "@/components/desktop/button/CustomButton";
import CustomProcessingModal from "@/components/desktop/modal/CustomProcessingModal";
import CustomTitle from "@/components/desktop/list/title/CustomTitle";

import useUserStore from "@/stores/useUserStore";
import { CHANGE_SEASON } from "@/redux/types/auth.types";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";

import { useCustomer, useCustomerSave, useCustomerExcelImport } from "@/hooks/customers/useCustomerForm";
import { useSeasonDelete } from "@/hooks/customers/useSeason";
import useSyncedFilter from "@/hooks/general/useSyncedFilter";

import { baseCustomer, getSeasonStatusAlerts, normalizeSeasonForSave, validateSeasons } from "./customer.constants";
import { getToday } from "@/services/Utils";

import classes from "./CustomerPage.module.scss";

const mapActiveSeasonToAuth = (seasons = []) => {
	const activeSeason = seasons.find((season) => season.active);
	if (!activeSeason) {
		return null;
	}

	return {
		value: activeSeason.id,
		label: activeSeason.name,
		initial_date: activeSeason.initial_date,
		end_date: activeSeason.end_date,
	};
};

const CustomerPage = () => {
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const inputRefExcel = useRef(null);
	const navigate = useNavigate();

	const { updateFilter } = useSyncedFilter();
	const user = useUserStore((state) => state.user);

	const [data, setData] = useState({ ...baseCustomer });
	const [isSavingLogo, setIsSavingLogo] = useState(false);

	const { data: response, isLoading } = useCustomer({ customer_id: user?.customer_id });
	const { mutate: saveCustomerData, isLoading: isLoadingSave } = useCustomerSave({ customer_id: user?.customer_id });
	const { mutate: deleteCustomerSeason, isLoading: isLoadingDelete } = useSeasonDelete({ customer_id: user?.customer_id });
	const { mutate: importExcel, isLoading: isLoadingExcelImport } = useCustomerExcelImport();

	const seasonAlerts = useMemo(() => getSeasonStatusAlerts(data?.seasons || []), [data?.seasons]);

	useEffect(() => {
		setData({ ...baseCustomer });
	}, [user?.customer_id]);

	useEffect(() => {
		if (response && response.customer) {
			setData(response.customer);
		}
	}, [response]);

	const selectSeason = (newSeason = null) => {
		const firstSeasonActive = data?.seasons?.find((season) => season.active);
		if (firstSeasonActive) {
			updateFilter({
				season_id: newSeason ? newSeason.id : firstSeasonActive.id,
			});
		}
	};

	const isProcessingSeasons = isLoadingSave || isLoadingDelete || isSavingLogo;
	const processingMessage = isSavingLogo ? "Guardando logo..." : isLoadingDelete ? "Eliminando temporada..." : "Guardando temporadas...";

	const buildCustomerFormData = (customerData, logoFile = null) => {
		const formData = new FormData();
		const seasonsPayload = (customerData.seasons || []).map(normalizeSeasonForSave);

		for (const key in customerData) {
			if (key === "seasons") {
				formData.append(key, JSON.stringify(seasonsPayload));
				continue;
			}

			if (Array.isArray(customerData[key])) {
				formData.append(key, JSON.stringify(customerData[key]));
			} else {
				formData.append(key, customerData[key] ?? "");
			}
		}

		if (logoFile) {
			formData.append("logo", logoFile);
		}

		return formData;
	};

	const handleSave = () => {
		const seasonsValidation = validateSeasons(data?.seasons || []);
		if (!seasonsValidation.valid) {
			toast.error(seasonsValidation.message);
			return;
		}

		saveCustomerData(buildCustomerFormData(data), {
			onSuccess: (saveResponse) => {
				const newSeasons = saveResponse?.customer?.seasons || [];
				const newSeason = newSeasons.find((season) => season.active);

				selectSeason(newSeason);
				dispatch({
					type: CHANGE_SEASON,
					payload: {
						season: mapActiveSeasonToAuth(newSeasons),
					},
				});

				toast.success("Temporadas guardadas correctamente");
			},
		});
	};

	const handleDeleteSeason = (index) => {
		const season = data?.seasons[index];

		deleteCustomerSeason(
			{ selected: season },
			{
				onSuccess: () => {
					const updatedSeasons = [...(data?.seasons || [])];
					updatedSeasons.splice(index, 1);

					selectSeason();
					dispatch({
						type: CHANGE_SEASON,
						payload: {
							season: mapActiveSeasonToAuth(updatedSeasons),
						},
					});
					setData((prev) => ({
						...prev,
						seasons: updatedSeasons,
					}));

					toast.success("Temporada eliminada correctamente");
				},
			},
		);
	};

	const handleImageChange = (event) => {
		const file = event.target.files[0];
		if (!file) {
			return;
		}

		const imageUrl = URL.createObjectURL(file);
		const updatedData = { ...data, logo: imageUrl };
		setData(updatedData);
		setIsSavingLogo(true);

		saveCustomerData(buildCustomerFormData(updatedData, file), {
			onSuccess: (saveResponse) => {
				if (saveResponse?.customer) {
					setData(saveResponse.customer);
				}
				toast.success("Logo actualizado");
			},
			onError: () => {
				toast.error("No se pudo guardar el logo. Intente nuevamente.");
			},
			onSettled: () => {
				setIsSavingLogo(false);
				if (inputRef.current) {
					inputRef.current.value = "";
				}
			},
		});
	};

	const handleExcelChange = () => {
		const file = inputRefExcel.current.files[0];
		const formData = new FormData();
		formData.append("file", file);

		importExcel(formData, {
			onSuccess: (importResponse) => {
				if (inputRefExcel.current) {
					inputRefExcel.current.value = null;
				}
				if (importResponse?.sheets && importResponse?.message) {
					toast.success(importResponse.message);
				}
			},
			onError: (error) => {
				if (inputRefExcel.current) {
					inputRefExcel.current.value = null;
				}

				const responseData = error?.response?.data;
				if (responseData?.detail) {
					console.error("[implementation import]", responseData.detail);
				}

				const userMessage = responseData?.data || "No se pudo cargar el Excel. Verifique el archivo e intente nuevamente.";
				toast.error(userMessage);
			},
		});
	};

	const handleExcelDownload = () => {
		const fileName = `${getToday()} Excel Global ${data?.name || user?.customer_name}.xlsx`;
		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: {},
					show: true,
					url: "/client/implementation/export_excel",
					name: fileName,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	if (isLoading || isLoadingExcelImport) return <Fetching />;

	return (
		<>
			<CustomProcessingModal open={isProcessingSeasons} message={processingMessage} />

			<CustomTitle
				title={[
					{ label: "Dashboard", onClick: () => navigate("/") },
					{ label: "Configuración", onClick: () => navigate("/configuracion") },
					{ label: "Cliente" },
				]}
				subtitle="Gestión de temporadas del cliente"
			/>

			<div className={classes.pageContent}>
				<div className={classes.customerCard}>
					<div className={classes.customerMain}>
						<div className={classes.logoContainer} onClick={() => !isSavingLogo && inputRef.current?.click()}>
							<img src={data?.logo || "https://placehold.co/120"} alt="Logo del cliente" className={classes.logo} />
							<input type="file" accept="image/*" ref={inputRef} style={{ display: "none" }} onChange={handleImageChange} />
						</div>
						<div className={classes.customerInfo}>
							<h2 className={classes.customerName}>{data?.name || ""}</h2>
							<p className={classes.customerHint}>Haz clic en el logo para cambiarlo. Se guarda automáticamente al seleccionar la imagen.</p>
						</div>
					</div>

					{!!user.superuser && (
						<div className={classes.excelSection}>
							<div className={classes.excelInfo}>
								<span className={classes.excelTitle}>Excel de configuración global</span>
								<p className={classes.excelHint}>Descarga o carga masiva la configuración base del cliente.</p>
							</div>
							<div className={classes.excelActions}>
								<CustomButton
									variant="secondary"
									size="sm"
									startIcon={<Download size={16} />}
									onClick={handleExcelDownload}
									disabled={isProcessingSeasons}
								>
									Descargar Excel
								</CustomButton>
								<CustomButton
									variant="secondary"
									size="sm"
									startIcon={<Upload size={16} />}
									onClick={() => inputRefExcel.current.click()}
									disabled={isProcessingSeasons}
								>
									Cargar Excel
								</CustomButton>
								<input ref={inputRefExcel} type="file" style={{ display: "none" }} onChange={() => handleExcelChange()} />
							</div>
						</div>
					)}
				</div>

				<Season
					seasons={data?.seasons || []}
					setSeasons={(seasons) => setData((prev) => ({ ...prev, seasons }))}
					handleDelete={handleDeleteSeason}
					alertGroups={seasonAlerts}
					onSave={handleSave}
					isSaving={isLoadingSave}
					isSaveDisabled={isProcessingSeasons}
				/>
			</div>
		</>
	);
};

export default CustomerPage;
